import skyguard from './products/skyguard';
import publicWarning from './products/publicWarning';
import nullNotification from './products/nullNotification';
import localStormReport from './products/localStormReport';
import forecastProduct from './products/forecastProduct';
import loadingProduct from './products/loadingProduct';
import {
  ENABLED_PRODUCT_TYPES,

  SKYGUARD,
  PUBLICWARNING,
  NULLNOTIFICATION,
  EARTHQUAKE,
  LOCALSTORMREPORT,
  LIGHTNINGPROXIMITY,
  STORMPOTENTIALNOTICE,
  STORMPOTENTIALOUTLOOK,
  GLOBALHAZARD,
  TROPICAL,
  UNSUPPORTED_PRODUCT,
} from './constants';

const factory = {};

export const getProductConstant = (productId) => {
  switch (productId) {
    case 1:
      return SKYGUARD;
    case 2:
      return PUBLICWARNING;
    case 3:
      return NULLNOTIFICATION;
    case 5:
      return EARTHQUAKE;
    case 6:
      return LOCALSTORMREPORT;
    // TODO: lightning proximity will show up in the list but should be ignored for now
    case 13:
      return LIGHTNINGPROXIMITY;

    case 8:
      return STORMPOTENTIALNOTICE;
    case 10:
      return STORMPOTENTIALOUTLOOK;
    case 12:
      return GLOBALHAZARD;
    case 14:
      return TROPICAL;

    default:
      return UNSUPPORTED_PRODUCT;
  }
};

factory.productHasHistory = (productTypeId, productDetails) => {
  if (productDetails === null) {
    return false;
  }

  if (productDetails.hasOwnProperty('status')) {
    return productDetails.status !== 'new';
  }
  switch (getProductConstant(productTypeId)) {
    case SKYGUARD:
    case PUBLICWARNING:
      // catch loading bugs
      if (productDetails.warnings !== undefined) {
        return productDetails.warnings.length > 1;
      }

      return false;
    default:
      return false;
  }
};

factory.checkShouldProductPlaySound = (productEvent, eventsPlayed = []) => {
  let shouldPlay = false;
  let shouldLoop = false;

  const { soundAction } = productEvent.content;
  switch (soundAction) {
    case 'mute':
      break;

    case 'play_until_read':
      if (productEvent.state.read !== true) {
        shouldPlay = true;
        shouldLoop = true;
      }
      break;

    case 'play_until_ack':
      if (productEvent.state.acked !== true) {
        shouldPlay = true;
        shouldLoop = true;
      }
      break;

    case 'play_once':
      shouldPlay = true;
      // check if its been played, if it has dont play it
      for (let j = 0; j < eventsPlayed.length; j++) {
        const eventP = eventsPlayed[j];
        if (eventP === productEvent.id) {
          shouldPlay = false;
        }
      }
      shouldLoop = false;
      break;

    // unrecegnized sound action, default to mute
    default:
      break;
  }

  return {
    shouldPlay,
    shouldLoop,
  };
};

// getProductSeverity
// gets the highest severity for a product
factory.getProductSeverity = (
  product,
  severities = [],
) => {
  if (!product) {
    return null;
  }

  const {
    criteria = [],
    severity,
  } = product;

  if (severity !== undefined) {
    return severity;
  }

  // public warnings have one criteria
  // this checks if its a public warning
  if (!Array.isArray(criteria)) {
    const s = severities.find((sev) => sev.id === criteria.severityId);
    if (s === undefined) {
      return null;
    }

    return s;
  }

  if (criteria.length === 0) {
    return null;
  }

  // get the highest priority
  const sortedByPriority = criteria.sort((a, b) => (a.priority > b.priority ? -1 : 1));

  const highestPriority = sortedByPriority[0];
  const highestPrioritySeverityId = highestPriority.severityId;
  const highestSeverity = severities.find((sev) => sev.id === highestPrioritySeverityId);

  // if it wasnt found return null
  if (highestSeverity === undefined) {
    return null;
  }

  return highestSeverity;
};

// sortBySeverity can be passed into sort and will sort based on severity priority
const sortBySeverity = (a, b, severities = []) => {
  if (!a || !b) {
    return false;
  }

  const severityA = severities.find((severity) => a.common.severityId === severity.id);
  const severityB = severities.find((severity) => b.common.severityId === severity.id);

  // this should catch anything with timing issues
  if (!severityA || !severityB) {
    return true;
  }

  return severityA.priority < severityB.priority;
};

// sort sorts a list of generic products by a compare function
const sort = (
  productGenericList = [],
  compareFunc,
  severities = [],
) => {
  if (typeof compareFunc !== 'function') return [];

  const sortedList = productGenericList.sort();

  for (let i = 0; i < sortedList.length; i++) {
    const elementToSort = sortedList[i];
    for (let j = i - 1; j >= 0; j--) {
      const sortedElement = sortedList[j];
      if (!compareFunc(sortedElement, elementToSort, severities)) {
        sortedList[j] = elementToSort;
        sortedList[j + 1] = sortedElement;
      }
    }
  }

  return sortedList;
};

factory.filterDisabledProductTypes = (productTypes) => {
  const filteredProductTypes = [];
  for (let i = 0; i < productTypes.length; i++) {
    if (ENABLED_PRODUCT_TYPES.indexOf(productTypes[i].id) !== -1) filteredProductTypes.push(productTypes[i]);
  }

  return filteredProductTypes;
};

export const generateGenericProduct = (
  productType,
  productEvent,
  specificProduct,
  playingSound,
  severities,
  userPreferences,
) => {
  if (productEvent && !specificProduct) {
    return loadingProduct(
      productType,
      productEvent,
      specificProduct,
      playingSound,
      severities,
      userPreferences,
    );
  }

  const constant = getProductConstant(productType.id);

  switch (constant) {
    case SKYGUARD:
      return skyguard(
        productType,
        productEvent,
        specificProduct,
        playingSound,
        severities,
        userPreferences,
      );
    case PUBLICWARNING:
      return publicWarning(
        productType,
        productEvent,
        specificProduct,
        playingSound,
        severities,
        userPreferences,
      );
    case NULLNOTIFICATION:
      return nullNotification(
        productType,
        productEvent,
        specificProduct,
        playingSound,
        severities,
        userPreferences,
      );
    case LOCALSTORMREPORT:
      return localStormReport(
        productType,
        productEvent,
        specificProduct,
        playingSound,
        severities,
        userPreferences,
      );
    case EARTHQUAKE:
    case STORMPOTENTIALNOTICE:
    case STORMPOTENTIALOUTLOOK:
    case GLOBALHAZARD:
    case TROPICAL:
      return forecastProduct(
        productType,
        productEvent,
        specificProduct,
        playingSound,
        severities,
        userPreferences,
      );
    case UNSUPPORTED_PRODUCT:
      return loadingProduct(
        productType,
        productEvent,
        specificProduct,
        playingSound,
        severities,
        userPreferences,
      );
    case LIGHTNINGPROXIMITY:
    default:
      return null;
  }
};

// NOTE: this is where you could see a bottleneck as far as speed
// takes the product event list and product details list and maps them into a generic list
// for the minimum amount of information to create the card deck
factory.computeProductGenericList = (allProductTypes, productEvents, productDetails,
  eventsPlayingSounds, eventsPlayed, severities, userPreferences) => {
  const genericProductList = [];
  productEvents.forEach((productEvent) => {
    const {
      id: productEventId,
      content,
    } = productEvent;
    const {
      productId,
    } = content;

    let details = productDetails[productEventId];
    if (details === undefined) details = null;

    const soundState = factory.checkShouldProductPlaySound(productEvent, eventsPlayed);
    const playingSound = soundState.shouldPlay;

    const product = generateGenericProduct(allProductTypes[productId], productEvent, details,
      playingSound, severities, userPreferences);

    if (product) {
      genericProductList.push(product);
    }
  });

  return sort(genericProductList, sortBySeverity, severities);
};

export default () => factory;
